import { useContext, useEffect, useState } from "react";
import { refreshContext } from "../contexts/refreshContext";
import { Accessibility } from "../models/Accessibility";
import { AccessibilityUnitLevel } from "../models/AccessibilityLevel";
import {
  AccessibilityUnit,
  AccessibilityUnitsQuery,
} from "../models/AccessibilityUnits";
import { Property } from "../models/Property";
import { PropertyAccessibilityImages } from "../models/PropertyAccessibilityImages";
import { AccessibilityInfo } from "../models/AccessibilityInfo";
import {
  retrievePropertyAccessibilityInfoByPropertyId,
  listAccessibilityImagesByPropertyId,
  retrieveAccessibilityByPropertyId,
  retrieveAccessibilityUnitsLevelsByPropertyId,
  retrieveAccessibilityUnits,
  retrievePropertiesByRelatedId,
  retrievePropertyAccessibilityByType,
  retrievePropertyFreeTextInfo,
  retrieveQuestionnaireStandards,
} from "../services/property.service";
import {replaceAccessibilitiesTitle, replaceNestedAccessibilities} from "../utils/accessibilities";
import {replaceNestedAccessibilityImages} from "../utils/images";
import {a11yStandardKeys, unitTypeKeys} from "../constants/constants";
import {PropertyFreeTextInfo} from "../models/PropertyFreeTextInfo";
import {Question} from "../models/Question";
import {retrieveQuestions} from "../services/questions.service";
import {A11yStandardType} from "../models/A11yStandard";

export const useFetchInfoFromProperty = ({ id, a11y_standard }: Property, standard: A11yStandardType) => {
  const [loading, setLoading] = useState(false);

  const [a11yStandard, setA11yStandard] = useState<A11yStandardType>(standard);

  const [parkingSpaces, setParkingSpaces] = useState<
    AccessibilityUnitLevel[] | undefined
  >(null!);
  const [toiletRoom, setToiletRoom] = useState<
    AccessibilityUnitLevel[] | undefined
  >(undefined);
  const [totalShop, setTotalShop] = useState<number | undefined>(undefined);

  const [accessibilities, setAccessibilities] = useState<Accessibility[] | undefined>([]);
  const [questions, setQuestions] = useState<Question[] | undefined>([]);

  const [generalAccessibilities, setGeneralAccessibilities] = useState<
    Accessibility[] | undefined
  >([]);
  const [hospitalityRooms, setHospitalityRooms] = useState<AccessibilityUnit[]>([]);
  const [attractions, setAttractions] = useState<AccessibilityUnit[]>([]);
  const [restaurants, setRestaurants] = useState<AccessibilityUnit[]>([]);
  const [mannedKiosks, setMannedKiosks] = useState<AccessibilityUnit[]>([]);
  const [meetingRooms, setMeetingRooms] = useState<AccessibilityUnit[]>([]);
  const [exhibitionRooms, setExhibitionRooms] = useState<AccessibilityUnit[]>([]);
  const [museumShopRooms, setMuseumShopRooms] = useState<AccessibilityUnit[]>([]);
  const [toiletCommonRooms, setToiletCommonRooms] = useState<AccessibilityUnit[]>([]);
  const [propertyImages, setPropertyImages] = useState<
    PropertyAccessibilityImages[]
  >([]);

  const [propImages, setPropImages] = useState<PropertyAccessibilityImages[] | undefined>(
    []
  );

  const [unitLevels, setUnitLevels] = useState<AccessibilityUnitLevel[]>([]);
  const [propertyAccessibilityInfo, setPropertyAccessibilityInfo] = useState<
    AccessibilityInfo[] | undefined
  >(null!);

  const [propertyFreeTextInfo, setPropertyFreeTextInfo] = useState<
    PropertyFreeTextInfo[] | undefined
  >(null!);
  const [accessibleUnits, setAccessibleUnits] = useState<AccessibilityUnit[]>(
    []
  );

  const [units, setUnits] = useState<AccessibilityUnitsQuery>();

  const [refresh] = useContext(refreshContext);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (standard && standard.key !== a11y_standard) {
        const response = await retrieveQuestionnaireStandards(a11y_standard);
        setA11yStandard(response[0])
      }
      setLoading(false);
    }
    fetchData().catch(console.error);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const propertyInfos = (
        await retrievePropertyAccessibilityInfoByPropertyId(id)
      ).results;
      setPropertyAccessibilityInfo(propertyInfos);
      setLoading(false);
    };
    fetchData().catch(console.error);
    return () => {
      setPropertyAccessibilityInfo(undefined);
      setLoading(false);
    };
  }, [refresh, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      if (!propertyFreeTextInfo) {
        const propertyFreeTextInfoResult = (
          await retrievePropertyFreeTextInfo(id)
        ).results
        setPropertyFreeTextInfo(propertyFreeTextInfoResult)
      }
      setLoading(false)
    }
    fetchData().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const propImgs = (await listAccessibilityImagesByPropertyId(id)).results;
      setPropImages(propImgs);
      setLoading(false);
    };
    fetchData().catch(console.error);
    return () => {
      setPropImages(undefined);
      setLoading(false);
    };
  }, [refresh, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const accessibilities = (
        await retrieveAccessibilityByPropertyId(
          id,
          "id,name,item_title,attribute_title,is_absent,accessibility_unit,attribute,attribute_icon,disability_types,item"
        )
      ).results;
      setGeneralAccessibilities(accessibilities);
    };
    fetchData().catch(console.error);
    setLoading(false);
    return () => {
      setGeneralAccessibilities(undefined);
      setLoading(false);
    };
  }, [refresh, id]);

  useEffect(() => {
    setLoading(true);
    if (generalAccessibilities && a11yStandard) {
      const filteredAccessibilities = generalAccessibilities.filter(
        (access) => access.is_absent === false
      );
      if (filteredAccessibilities) {
        // the units, not accessibleUnits: those are set by another effect and can still be empty here
        const allUnits = units?.results ?? []
        const filterOutNull = filteredAccessibilities.filter((i) => i.attribute_title !== null)
        const replacedAccessibilitiesTitle = replaceAccessibilitiesTitle(filterOutNull, allUnits, a11yStandard)
        const replacedNestedAccessibilities = replaceNestedAccessibilities(replacedAccessibilitiesTitle, allUnits)
        setAccessibilities(replacedNestedAccessibilities);
      }
      setLoading(false);
    }
    return () => {
      setAccessibilities(undefined);
      setLoading(false);
    };
  }, [refresh, generalAccessibilities, a11yStandard, units]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const unitLevels = (await retrieveAccessibilityUnitsLevelsByPropertyId(id))
        .results;
      setUnitLevels(unitLevels);
      setLoading(false);
    };
    fetchData().catch(console.error);
  }, [refresh, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const units = await retrieveAccessibilityUnits(id);
      setUnits(units);
    };

    fetchData().catch(console.error);
    return () => {
      setUnits(undefined);
      setLoading(false);
    };
  }, [refresh, id]);

  useEffect(() => {
    if (units) {
      const rooms = units.results.filter(({type}) => type === unitTypeKeys.hotelRoomType);
      const exhibitionRooms = units.results.filter(({ type }) => type === unitTypeKeys.exhibition);
      const museumShops = units.results.filter(({ type }) => type === unitTypeKeys.museumShop);
      const toiletRoomCommon = units.results.filter(({ type }) => type === unitTypeKeys.toiletRoomCommon);
      const attractions = units.results.filter(({ type }) => type === unitTypeKeys.attraction);
      const restaurants = units.results.filter(({ type }) => type === unitTypeKeys.restaurant);
      const kiosks = units.results.filter(({ type }) => type === unitTypeKeys.mannedKiosk);

      setExhibitionRooms(exhibitionRooms)
      setAttractions(attractions)
      setMannedKiosks(kiosks)
      setRestaurants(restaurants)
      setHospitalityRooms(rooms);
      setMuseumShopRooms(museumShops);
      setToiletCommonRooms(toiletRoomCommon);
    }
  }, [refresh, units]);

  useEffect(() => {
    if (units) {
      const rooms = units.results.filter(({ type }) => type === unitTypeKeys.meetingRoom);
      setMeetingRooms(rooms);
    }
  }, [refresh, units]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (a11y_standard === a11yStandardKeys.shoppingMall) {
        const relatedPropertiesCount = (await retrievePropertiesByRelatedId(id))
          .count;
        setTotalShop(relatedPropertiesCount);
        setLoading(false);
      }
    };

    fetchData().catch(console.error);
    return () => {
      setTotalShop(0);
      setLoading(false);
    };
  }, [refresh, id, a11y_standard]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const parkingSpaces = (
        await retrievePropertyAccessibilityByType(
          id,
          unitTypeKeys.parking,
          "number_of_units"
        )
      ).results;
      setParkingSpaces(parkingSpaces);
      setLoading(false);
    };

    fetchData().catch(console.error);
    return () => {
      setParkingSpaces(undefined);
      setLoading(false);
    };
  }, [refresh, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const questions = (
        await retrieveQuestions()
      ).results;
      setQuestions(questions);
      setLoading(false);
    };

    fetchData().catch(console.error);
    return () => {
      setQuestions(undefined);
      setLoading(false);
    };
  }, [refresh, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result = (
        await retrievePropertyAccessibilityByType(
          id,
          unitTypeKeys.toiletRoomCommon,
          "number_of_units,accessibility_unit"
        )
      ).results;
      setToiletRoom(result);
      setLoading(false);
    };

    fetchData().catch(console.error);
    return () => {
      setToiletRoom(undefined);
      setLoading(false);
    };
  }, [refresh, id]);

  useEffect(() => {
    setLoading(true);
    // accessibilities is only undefined while they are recomputed
    if (accessibilities && units) {
      if (units.results.length > 0) {
        setAccessibleUnits([...units.results].sort((a, b) => a.order - b.order));
      }
      setLoading(false);
    }
    return () => {
      setAccessibleUnits([]);
      setLoading(false)
    };
  }, [refresh, accessibilities, units]);

  useEffect(() => {
    if (propImages && units) {
      const shownImages = propImages.filter((el) => el.is_display_in_output_service === true);
      // the units, not accessibleUnits: those are set by another effect and can still be empty here
      setPropertyImages(replaceNestedAccessibilityImages(shownImages, units.results));
    }
  }, [units, propImages]);

  return {
    parkingSpaces,
    toiletRoom,
    totalShop,
    accessibilities,
    attractions,
    restaurants,
    mannedKiosks,
    hospitalityRooms,
    meetingRooms,
    exhibitionRooms,
    museumShopRooms,
    toiletCommonRooms,
    propertyImages,
    unitLevels,
    propertyAccessibilityInfo,
    propertyFreeTextInfo,
    accessibleUnits,
    units,
    loading,
    questions,
    a11yStandard
  };
};
